import { useContext, createContext, useState } from "react";
import { baseURL } from "../lib";
import { useAuth } from "./AuthContext";
import axios from 'axios'

const CategoryContext = createContext()

const CategoryProvider = ({ children }) => {
    const { headers, toast, restaurantId } = useAuth();
    const [isDraggable, setIsDraggable] = useState(false)

    const getAllCategories = async (filter) => {
        try {
            const { modifiers, limit, totalRenderedRecords, filter: filterCategory, restaurant_id } = filter

            let { data } = await axios.get(`${baseURL}/category/list?modifiers=${modifiers ? JSON.stringify(modifiers) : null}&limit=${limit ? limit : null}&totalRenderedRecords=${totalRenderedRecords >= 0 ? totalRenderedRecords : null}&restaurant_id=${restaurantId ? restaurantId : null}&filter=${filterCategory !== '' ? filterCategory : null}&is_web=${true}`, { headers })
            if (data.error === false) {
                return data.data
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Category', detail: 'An error occurred. Please try again later.', life: 3000 })
        }
    }

    const getCategoriesList = async (restaurant_id) => {
        try {
            const { data } = await axios.get(`${baseURL}/category/list-without-pagination?restaurant_id=${restaurant_id}`, { headers });

            if (data.error === false) {
                return data.data;
            }
        } catch (error) {
            toast.current?.show({
                severity: 'error',
                summary: 'Category',
                detail: 'An error occurred. Please try again later.',
                life: 3000,
            });
        }
    };

    // Fetches categories that are available for assignment to printers on a specific floor.
    const getAvailableCategoriesForPrinterByFloor = async ({
        restaurant_id,
        floor_id,
        filter = "",
        limit = 50,
        offset = 0,
    }) => {
        try {
            const { data } = await axios.get(
                `${baseURL}/category/available-for-printer`,
                {
                    headers,
                    params: {
                        restaurant_id,
                        floor_id,
                        filter,
                        limit,
                        offset,
                    },
                }
            );            
    
            if (data?.error === false) {
                return data.data; // { categories, totalCount }
            }
    
            return {
                categories: [],
                totalCount: 0,
            };
        } catch (error) {
            console.error("Error fetching available categories for printer:", error);
    
            toast.current?.show({
                severity: "error",
                summary: "Category",
                detail: "Failed to load categories. Please try again later.",
                life: 3000,
            });
    
            return {
                categories: [],
                totalCount: 0,
            };
        }
    };    

    //Get Single Category
    const getSingleCategory = async (id) => {
        try {
            let { data } = await axios.get(`${baseURL}/category/single-category/${id}`, { headers })
            if (data.error === false) {
                return data.data
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Category', detail: 'An error occurred. Please try again later.', life: 3000 })
        }
    }

    //Create Category
    const createCategory = async (categoryDetail) => {
        try {
            const { data } = await axios.post(`${baseURL}/category/create`, categoryDetail, { headers });
            if (data.error === false) {
                setTimeout(function () {
                    toast.current?.show({ severity: 'success', summary: 'Category', detail: data.message, life: 3000 })
                }, 500);
                return data
            } else {
                toast.current?.show({ severity: 'error', summary: 'Category', detail: data.message, life: 3000 })
            }
        } catch (error) {
            if (error.response) {
                const errors = error.response.data.errors;
                if (errors && Array.isArray(errors) && errors.length > 0) {
                    if (errors.length > 1) {
                        toast.current?.show({ severity: 'error', summary: 'Category', detail: "Please fill all mandatory fields.", life: 3000 })
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Category', detail: errors[0].msg, life: 3000 })
                    }
                }
            } else {
                toast.current?.show({ severity: 'error', summary: 'Category', detail: 'An error occurred. Please try again later.', life: 3000 })
            }
        }
    }

    //Update Category
    const updateCategory = async (id, categoryDetail) => {
        try {
            const { data } = await axios.put(`${baseURL}/category/update/${id}`, categoryDetail, { headers });
            if (data.error === false) {
                setTimeout(function () {
                    toast.current?.show({ severity: 'success', summary: 'Category', detail: data.message, life: 3000 })
                }, 500);
                return data
            } else {
                toast.current?.show({ severity: 'error', summary: 'Category', detail: data.message, life: 3000 })
            }
        } catch (error) {
            if (error.response) {
                const errors = error.response.data.errors;
                if (errors && Array.isArray(errors) && errors.length > 0) {
                    if (errors.length > 1) {
                        toast.current?.show({ severity: 'error', summary: 'Category', detail: "Please fill all mandatory fields.", life: 3000 })
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Category', detail: errors[0].msg, life: 3000 })
                    }
                }
            } else {
                toast.current?.show({ severity: 'error', summary: 'Category', detail: 'An error occurred. Please try again later.', life: 3000 })
            }
        }
    }

    //Update Category postions
    const updateCategoryPositions = async (positions) => {
        try {
            const { data } = await axios.post(`${baseURL}/category/update-positions`, { positions }, { headers });
            if (data.error === false) {
                return data
            } else {
                toast.current?.show({ severity: 'error', summary: 'Category', detail: data.message, life: 3000 })
            }
        } catch (error) {
            if (error.response) {
                const errors = error.response.data.errors;
                if (errors && Array.isArray(errors) && errors.length > 0) {
                    if (errors.length > 1) {
                        toast.current?.show({ severity: 'error', summary: 'Category', detail: "Please fill all mandatory fields.", life: 3000 })
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Category', detail: errors[0].msg, life: 3000 })
                    }
                }
            } else {
                toast.current?.show({ severity: 'error', summary: 'Category', detail: 'An error occurred. Please try again later.', life: 3000 })
            }
        }
    }

    //Delete Category
    const deleteCategory = async (id) => {
        try {
            const { data } = await axios.delete(`${baseURL}/category/delete/${id}`, { headers });
            if (data.error === false) {
                setTimeout(function () {
                    toast.current?.show({ severity: 'success', summary: 'Category', detail: data.message, life: 3000 })
                }, 500);
                return data
            } else {
                toast.current?.show({ severity: 'error', summary: 'Category', detail: data.message, life: 3000 })
                return data;
            }
        } catch (error) {
            toast.current?.show({ severity: 'error', summary: 'Category', detail: 'An error occurred. Please try again later.', life: 3000 })
        }
    }

    return (
        <CategoryContext.Provider value={{ getAllCategories, getCategoriesList, getAvailableCategoriesForPrinterByFloor, createCategory, updateCategory, updateCategoryPositions, getSingleCategory, deleteCategory, isDraggable, setIsDraggable }}>
            {children}
        </CategoryContext.Provider>
    )
}

const useCategory = () => useContext(CategoryContext)

export { useCategory, CategoryProvider }